import { toast } from 'sonner';
import { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import { appStoreActions } from '@/stores';
import { Message, ResultMessage } from '@copilotkit/runtime-client-gql';

// Utils
import { showErrorToast } from '@/utils/errorHandlers';

// Constants
import { ActionResult, handleAction } from '@/components/SearchForm/action';
import {
  CONFIRM_MESSAGE_CONFIG,
  PROJECT_CODE_REGEX,
  ROUTES,
  SEARCH_FORM_MESSAGE,
  USERNAME_PLACEHOLDER_REGEX,
} from '@shared/constants';
import {
  CHARACTER_MENTION_TRIGGER_LIST,
  CHATBOT_MESSAGES,
  MENTION_PROJECT_CHARACTER,
  MENTION_USER_CHARACTER,
} from '@/constants';
import { AGILITYIO_TEMPLATE_INFO } from '@/components/template/resume/AgilityIO/pdf';

// Types
import { GetToken } from '@/types';
import {
  ActionGroup,
  ActionItemType,
  ActionKind,
  ActivityType,
} from '@shared/types';

interface HandleActionClickParams {
  item: ActionItemType;
  router: AppRouterInstance;
  requestConfig?: {
    getToken: GetToken;
    userEmail: string;
  };
  onSuccessAction?: () => void;
  onClose?: () => void;
  setSearchQuery: (query: string) => void;
  setCursorPosition?: (position: number) => void;
}

export const handleActionClick = async ({
  item,
  router,
  requestConfig,
  onSuccessAction,
  onClose,
  setSearchQuery,
  setCursorPosition,
}: HandleActionClickParams) => {
  const FILTER_PATTERNS = [USERNAME_PLACEHOLDER_REGEX, PROJECT_CODE_REGEX];

  // Find the first matching pattern in the item label
  const matchedPattern = FILTER_PATTERNS.find((pattern) =>
    item.label?.match(pattern),
  );

  if (matchedPattern) {
    // Determine replacement based on whether label contains @
    const replacement =
      CHARACTER_MENTION_TRIGGER_LIST.find((trigger) =>
        item.label?.includes(trigger),
      ) || '';
    const updatedText = item.label?.replace(matchedPattern, replacement);

    setSearchQuery(updatedText ?? '');

    // Set cursor position after @ symbol or at the end
    if (replacement === MENTION_USER_CHARACTER) {
      const atIndex = (updatedText ?? '').indexOf(MENTION_USER_CHARACTER);
      setCursorPosition?.(
        atIndex !== -1 ? atIndex + 1 : (updatedText ?? '').length,
      );
    }

    if (replacement === MENTION_PROJECT_CHARACTER) {
      {
        const atIndex = (updatedText ?? '').indexOf(MENTION_PROJECT_CHARACTER);

        setCursorPosition?.(
          atIndex !== -1 ? atIndex + 1 : (updatedText ?? '').length,
        );
      }
    }

    return;
  }

  // For REDIRECT actions from keyboard, trigger click to let Link handle navigation
  if (item.kind === ActionKind.REDIRECT) {
    const pathname = getRedirectPath(item);

    if (pathname.includes(ROUTES.PREVIEW)) {
      // Open preview with AgilityIO template in new tab
      window.open(
        `${pathname}?templateId=${AGILITYIO_TEMPLATE_INFO.id}`,
        '_blank',
      );
    } else {
      // Reset chat before navigation
      const setChatResetFlag = appStoreActions.setChatResetFlag;
      setChatResetFlag(true);
      router.push(pathname);
    }

    if (shouldCloseDialog(item)) {
      onClose?.();
    }

    return;
  }

  try {
    const { getToken, userEmail } = requestConfig || {};
    const token = await getToken?.();

    // Handle regular action
    const result: ActionResult = await handleAction({
      item,
      router,
      requestConfig: {
        token,
        userEmail,
      },
      callback: {
        onSuccess: onSuccessAction,
      },
    });

    // Handle toast notifications based on action result
    showActionResultToast(result);

    // Close dialog if it's a redirect action
    if (shouldCloseDialog(item)) {
      onClose?.();
    }
  } catch {
    toast.error(SEARCH_FORM_MESSAGE.EXECUTE_ACTION_FAILED);
  }
};

/**
 * Groups options by their group property
 * @param options Array of action items to be grouped
 * @returns Object with group names as keys and arrays of matching options as values
 */
export const groupOptionsByGroup = (
  options: ActionItemType[],
): Record<string, ActionItemType[]> => {
  return options.reduce(
    (acc, option) => {
      const groupKey = String(option.group);
      (acc[groupKey] ??= []).push(option);

      return acc;
    },
    {} as Record<string, ActionItemType[]>,
  );
};

/**
 * Determines if the search dialog should be closed based on the action item
 *
 * @param item The action item that was clicked
 * @returns boolean indicating whether the dialog should be closed
 */
export const shouldCloseDialog = (item: ActionItemType): boolean => {
  // Special case: Close dialog for CREATE actions even though they are ACTION type
  if (
    (item.kind === ActionKind.ACTION &&
      item.type === ActivityType.RESUME_CREATE) ||
    (item.kind === ActionKind.REDIRECT && item.group === ActionGroup.EMPLOYEE)
  ) {
    return true;
  }

  // Keep dialog open for these cases
  if (
    item.kind === ActionKind.ACTION ||
    item.type === ActivityType.RESUME_DOWNLOAD ||
    item.type === ActivityType.RESUME_DOWNLOAD_MY ||
    item.type === ActivityType.RESUME_DOWNLOAD_DOCX ||
    item.type === ActivityType.RESUME_DOWNLOAD_MY_DOCX ||
    (item.kind === ActionKind.REDIRECT &&
      item.label?.toLowerCase().includes('resume') &&
      item.label?.toLowerCase().includes('view'))
  ) {
    return false;
  }

  return true;
};

/**
 * Determines if an action item should open in a new browser tab
 *
 * @param item The action item to evaluate
 * @returns boolean indicating whether the item should open in a new tab
 */
export const shouldOpenInNewTab = (item: ActionItemType): boolean => {
  // Only keep dialog open for "view resume" actions
  if (
    item.kind === ActionKind.REDIRECT &&
    item.label?.toLowerCase().includes('resume') &&
    item.label?.toLowerCase().includes('view')
  ) {
    return true;
  }

  return false;
};

/**
 * Determines the redirect path for an action item based on keywords in its label
 *
 * @param item The action item containing label and payload information
 * @returns The matching route path if keywords are found in the label, otherwise '#'
 */
export const getRedirectPath = (item: ActionItemType) => {
  if (item.group === ActionGroup.PROJECT)
    return `${ROUTES.PROJECTS}/${item.identity}`;

  if (item.group === ActionGroup.EMPLOYEE)
    return `${ROUTES.EMPLOYEES}/${item.identity}`;

  if (item.group === ActionGroup.TRAINING)
    return `${ROUTES.EMPLOYEE_TRAININGS}/${item.identity}`;

  // Maps route paths to their identifying keywords - all keywords must be present in the item label for a match
  const ROUTE_KEYWORD_MAP = {
    [`${ROUTES.RESUME}/${item.identity}${ROUTES.PERSONAL_INFORMATION}`]: [
      'edit',
      'resume',
    ],
    [`${ROUTES.RESUME}/${item.identity}${ROUTES.PREVIEW}`]: ['view', 'resume'],
  };

  const itemLabel = item.label?.toLowerCase();

  // Find the path where all keywords are present in the item label
  for (const [path, keywords] of Object.entries(ROUTE_KEYWORD_MAP)) {
    const allKeywordsPresent = keywords.every((keyword) =>
      itemLabel?.includes(keyword.toLowerCase()),
    );

    if (allKeywordsPresent) {
      return path;
    }
  }

  return '#';
};

/**
 * Handles displaying toast notifications based on action result
 * Uses showErrorToast for errors and toast.success for success cases
 *
 * @param result The action result containing success status and optional message/error
 */
export const showActionResultToast = (result: ActionResult) => {
  if (!result.success) {
    showErrorToast(result.error);
    return;
  }

  if (result.message) {
    toast.success(result.message);
  }
};

interface ConfirmationConfig {
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

export const getConfirmationConfig = (
  item: ActionItemType,
): ConfirmationConfig | undefined => {
  const {
    CONFIRM_ARCHIVED_PROJECT,
    CONFIRM_DEACTIVATE_USER,
    DEFAULT_CANCEL_LABEL,
  } = CONFIRM_MESSAGE_CONFIG;

  if (!item || !item.identity) return;

  if (item.type === ActivityType.USER_DEACTIVATE)
    return {
      message: CONFIRM_DEACTIVATE_USER.message(item.identity),
      confirmLabel: CONFIRM_DEACTIVATE_USER.confirmLabel,
      cancelLabel: DEFAULT_CANCEL_LABEL,
    };

  if (item.type === ActivityType.PROJECT_ARCHIVE)
    return {
      message: CONFIRM_ARCHIVED_PROJECT.message(item.identity),
      confirmLabel: CONFIRM_ARCHIVED_PROJECT.confirmLabel,
      cancelLabel: DEFAULT_CANCEL_LABEL,
    };
};

export const stripJsonSuffix = (content?: string) => {
  if (!content) {
    return '';
  }

  const trimmedEnd = content.trimEnd();

  if (!trimmedEnd || trimmedEnd.startsWith('```')) {
    return content;
  }

  const lastObjectIndex = trimmedEnd.lastIndexOf('{');
  const lastArrayIndex = trimmedEnd.lastIndexOf('[');
  const jsonStartIndex = Math.max(lastObjectIndex, lastArrayIndex);

  if (jsonStartIndex < 0) {
    return content;
  }

  const jsonCandidate = trimmedEnd.slice(jsonStartIndex);

  try {
    const parsed = JSON.parse(jsonCandidate);
    if (parsed !== null && typeof parsed === 'object') {
      return trimmedEnd.slice(0, jsonStartIndex).trimEnd();
    }
  } catch {
    return content;
  }

  return content;
};

const formalizeMsgContent = (content: string, actionLabel: string): string => {
  const sanitized = content.replace(/\s?\[.*?\]\(#\)\s?/g, '');

  // Agent used TypeScript code blocks to encode action data — replace with user-facing success message.
  if (content.includes('```ts') && actionLabel) {
    return CHATBOT_MESSAGES.SEARCH_SUCCESSFULLY(actionLabel);
  }

  return sanitized || CHATBOT_MESSAGES.SEARCH_SUCCESSFULLY(actionLabel);
};

export const getMessageDisplayContent = (
  msg: Message,
  actionLabel: string,
): string | null => {
  if (msg.isTextMessage()) {
    if (msg.role === 'user' || !msg.content) return null;
    const stripped = stripJsonSuffix(msg.content);
    return stripped ? formalizeMsgContent(stripped, actionLabel) : null;
  }

  if (msg.isResultMessage()) {
    try {
      const parsed = ResultMessage.decodeResult(msg.result);
      // Handler actions run async — don't show pre-emptive success;
      // the action's own toast handles the outcome.
      return (
        (typeof parsed?.validation === 'string' && parsed.validation) ||
        (typeof parsed?.success === 'string' && parsed.success) ||
        null
      );
    } catch {
      return null;
    }
  }

  return null;
};
